import { useEffect, useMemo, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  Box,
  Building2,
  Check,
  ChevronDown,
  ChevronRight,
  DoorOpen,
  Layers,
  MapPin,
  Pencil,
  Plus,
  Search,
  Trash2,
  X,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { cn } from '@/lib/utils'
import {
  useCreateRoom,
  useCreateRoomType,
  useFacilityMutations,
  useRenameRoom,
  useRoomTypes,
  useSetEquipmentRoom,
} from '@/hooks/useFacility'
import { allRooms, formatPlace, indexRooms } from '@/lib/facilityLocation'
import { buildingCodeFrom, floorName, nextFloorUp, nextRoomLabel } from '@/lib/facilityStudio'
import { moveFloor, roomFootprint } from '@/lib/floorIsometric'
import { UNIT_SHAPES, UNIT_SHAPE_LABELS, guessShape, shapeOf, type UnitShape } from '@/lib/unitShapes'
import { storageOutline, type OutlineNode } from '@/lib/storageOutline'
import { ROOM_OUTLINES, ROOM_OUTLINE_LABELS, toOutline } from '@/lib/roomOutline'
import { useStorageSubtree } from '@/hooks/useStorage'
import type { LocationRoom, LocationTree, PlacedUnit } from '@/api/facilityApi'
import type { CapacitySummaryRow } from '@/types/storage'
import { BuildingElevation, ViewToggle, type ExplorerView } from './BuildingElevation'
import { BuildingIsometric, FloorIsometric, RoomIsometric } from './FloorIsometric'

type Facility = LocationTree['facilities'][number]
type Building = Facility['buildings'][number]
type Floor = Building['floors'][number]
type Run = (fn: () => Promise<unknown>) => Promise<void>

/** Where a piece of equipment is in the tree, by ids. */
export function findUnit(tree: LocationTree, equipmentId: string) {
  for (const f of tree.facilities) {
    for (const b of f.buildings) {
      for (const fl of b.floors) {
        const room = fl.rooms.find((r) => r.units.some((u) => u.id === equipmentId))
        if (room) return { facilityId: f.id, buildingId: b.id, floorId: fl.id, roomId: room.id }
      }
    }
  }
  const loose = tree.unmappedRooms.find((r) => r.units.some((u) => u.id === equipmentId))
  return loose ? { facilityId: null, buildingId: null, floorId: null, roomId: loose.id } : null
}

/**
 * The facility explorer (z8nrz7fw25): buildings and their floors down the
 * side, the chosen floor in the middle — in 3D, with its rooms as cards below —
 * and the chosen room on the right with what stands in it.
 *
 * `focusUnitId` is the storage unit this was opened from: the explorer opens on
 * its building, floor and room and marks it as "This unit" everywhere it
 * appears. `editable` is false for someone who may look but not change the
 * layout; every add, rename and remove control then stays out of the way.
 */
export function FacilityExplorer({
  tree,
  facility,
  byEquipment,
  focusUnitId,
  editable,
  run,
}: {
  tree: LocationTree
  facility: Facility
  byEquipment: Map<string, CapacitySummaryRow>
  focusUnitId?: string | null
  editable: boolean
  run: Run
}) {
  const [buildingId, setBuildingId] = useState<string | null>(null)
  const [floorId, setFloorId] = useState<string | null>(null)
  const [roomId, setRoomId] = useState<string | null>(null)
  const [expanded, setExpanded] = useState<Set<string>>(new Set())
  // The whole building first: its floors and rooms at a glance.
  const [view, setView] = useState<ExplorerView>('building')
  // The unit picked inside a room: lit in the room view and in the room's list.
  const [unitId, setUnitId] = useState<string | null>(null)

  // Open on the unit this was opened from — once, and only when it is in
  // this facility. A reload after an edit, or a look at another facility,
  // must never pull the view back to the unit.
  const landedOn = useRef<string | null>(null)
  useEffect(() => {
    if (!focusUnitId || landedOn.current === focusUnitId) return
    const at = findUnit(tree, focusUnitId)
    if (!at) return
    landedOn.current = focusUnitId
    if (at.facilityId !== facility.id) return
    if (at.buildingId) {
      setBuildingId(at.buildingId)
      setExpanded((s) => new Set(s).add(at.buildingId as string))
    }
    setFloorId(at.floorId)
    setRoomId(at.roomId)
  }, [focusUnitId, tree, facility.id])

  const building = facility.buildings.find((b) => b.id === buildingId) ?? facility.buildings[0] ?? null
  const floorsTopDown = building ? [...building.floors].sort((a, b) => b.level - a.level) : []
  const floor =
    floorsTopDown.find((f) => f.id === floorId) ?? floorsTopDown.find((f) => f.rooms.length > 0) ?? floorsTopDown[0] ?? null
  const room = roomId ? (allRooms(tree).find((r) => r.id === roomId) ?? null) : null
  const isExpanded = (id: string) => expanded.has(id) || id === building?.id
  // Room 3D needs a room; leaving the room leaves its view.
  useEffect(() => {
    if (!room && view === 'room') setView('building')
  }, [room, view])
  const roomSize = room ? roomFootprint({ areaSqm: room.areaSqm ?? null, unitCount: room.units.length, size: room.size ?? null }) : null

  const mut = useFacilityMutations()
  // Dragging a room on its floor saves where it sits.
  const moveRoom = editable
    ? (rid: string, x: number, y: number) => void run(() => mut.setRoomPosition.mutateAsync({ roomId: rid, x, y }))
    : undefined
  // Dragging a floor up or down the stack: the floors keep the building's
  // storey numbers in their new order, and a floor still called by its storey
  // ("2nd Floor") is renamed to match. Names go to a placeholder first, so two
  // floors swapping names never clash on the way.
  const shiftFloor =
    editable && building
      ? (fid: string, steps: number) =>
          void run(async () => {
            const levels = building.floors.map((f) => f.level).sort((a, b) => a - b)
            const changes = moveFloor(building.floors, fid, steps)
              .map((f, i) => ({ f, level: levels[i] }))
              .filter(({ f, level }) => f.level !== level)
              .map(({ f, level }) => ({ id: f.id, level, rename: f.name === floorName(f.level) ? floorName(level) : null }))
            for (const c of changes) {
              await mut.updateFloor.mutateAsync({
                id: c.id,
                level: c.level,
                ...(c.rename ? { name: `${c.rename} (moving ${c.id.slice(-4)})` } : {}),
              })
            }
            for (const c of changes) if (c.rename) await mut.updateFloor.mutateAsync({ id: c.id, name: c.rename })
            setFloorId(fid)
          })
      : undefined

  return (
    <div className='grid min-h-0 flex-1 grid-cols-1 md:grid-cols-[260px_minmax(0,1fr)] xl:grid-cols-[260px_minmax(0,1fr)_340px]'>
      <BuildingTree
        facility={facility}
        selectedBuildingId={building?.id ?? null}
        selectedFloorId={floor?.id ?? null}
        isExpanded={isExpanded}
        onToggle={(id) =>
          setExpanded((s) => {
            const next = new Set(s)
            if (next.has(id)) next.delete(id)
            else next.add(id)
            return next
          })
        }
        onSelectBuilding={(id) => {
          setBuildingId(id)
          setFloorId(null)
          setExpanded((s) => new Set(s).add(id))
        }}
        onSelectFloor={(bId, fId) => {
          setBuildingId(bId)
          setFloorId(fId)
        }}
        unmappedRooms={tree.unmappedRooms}
        onSelectRoom={setRoomId}
        selectedRoomId={roomId}
        editable={editable}
        run={run}
      />

      <main className='min-h-0 space-y-5 overflow-y-auto bg-slate-50/70 p-5'>
        {!building ? (
          <EmptyState
            title={`${facility.name} has no buildings yet`}
            text={editable ? 'Add the first building with + on the left.' : 'Buildings are added in the facility set-up.'}
          />
        ) : (
          <>
            <FloorHeader
              facility={facility}
              building={building}
              floor={floor}
              byEquipment={byEquipment}
              view={view}
              onViewChange={setView}
              roomEnabled={!!room}
              editable={editable}
              run={run}
            />

            {view === 'room' && room && roomSize ? (
              <div className='relative'>
                <RoomIsometric
                  room={room}
                  width={roomSize.w}
                  depth={roomSize.d}
                  selectedUnitId={unitId}
                  onSelectUnit={setUnitId}
                  onMoveUnit={editable ? (id, x, y) => void run(() => mut.setUnitSpot.mutateAsync({ equipmentId: id, x, y })) : undefined}
                  highlightUnitId={focusUnitId}
                  className='min-h-[440px] border border-slate-200 bg-white'
                />
                <p className='absolute left-3 top-3 rounded-lg border bg-white/95 px-3 py-1.5 text-sm shadow-sm'>
                  <span className='font-semibold'>{room.label}</span>
                  {room.name && <span className='text-slate-500'>, {room.name}</span>}
                  {editable && <span className='block text-[11px] text-slate-500'>Drag a unit to move it. Pick its shape on the right.</span>}
                </p>
              </div>
            ) : view === 'building' ? (
              building.floors.length ? (
                <div className='relative'>
                  <BuildingIsometric
                    floors={building.floors}
                    selectedFloorId={floor?.id ?? null}
                    onSelectFloor={setFloorId}
                    onMoveFloor={shiftFloor}
                    onMoveRoom={moveRoom}
                    selectedRoomId={room?.id ?? null}
                    onSelectRoom={setRoomId}
                    highlightUnitId={focusUnitId}
                    className='min-h-[420px] border border-slate-200 bg-white'
                  />
                  <Legend focus={!!focusUnitId} />
                </div>
              ) : (
                <EmptyState
                  title={`${building.name} has no floors yet`}
                  text={editable ? 'Add a floor under the building on the left.' : ''}
                />
              )
            ) : view === '3d' ? (
              floor ? (
                <div className='relative'>
                  {floor.rooms.length ? (
                    <FloorIsometric
                      rooms={floor.rooms}
                      selectedRoomId={room && floor.rooms.some((r) => r.id === room.id) ? room.id : null}
                      onSelectRoom={setRoomId}
                      onMoveRoom={moveRoom}
                      highlightUnitId={focusUnitId}
                      className='min-h-[360px] border border-slate-200 bg-white'
                    />
                  ) : (
                    <EmptyState title={`No rooms on ${floor.name} yet`} text={editable ? 'Add one below.' : ''} />
                  )}
                  {floor.rooms.length > 0 && <Legend focus={!!focusUnitId} />}
                </div>
              ) : (
                <EmptyState
                  title={`${building.name} has no floors yet`}
                  text={editable ? 'Add a floor under the building on the left.' : ''}
                />
              )
            ) : (
              <BuildingElevation
                building={building}
                facilityLabel={`${facility.code} · ${facility.name}`}
                byEquipment={byEquipment}
                selectedRoomId={roomId}
                onSelectRoom={setRoomId}
                compact
              />
            )}

            {floor && (
              <RoomsOnFloor
                floor={floor}
                tree={tree}
                byEquipment={byEquipment}
                selectedRoomId={roomId}
                onSelectRoom={setRoomId}
                focusUnitId={focusUnitId ?? null}
                editable={editable}
                run={run}
              />
            )}
          </>
        )}
      </main>

      <RoomPanel
        tree={tree}
        facility={facility}
        room={room}
        byEquipment={byEquipment}
        focusUnitId={focusUnitId ?? null}
        selectedUnitId={unitId}
        onSelectUnit={setUnitId}
        onArrange={() => setView('room')}
        editable={editable}
        onClose={() => setRoomId(null)}
        run={run}
      />
    </div>
  )
}

// ── Left: buildings and their floors ─────────────────────────────────────────

function BuildingTree({
  facility,
  selectedBuildingId,
  selectedFloorId,
  isExpanded,
  onToggle,
  onSelectBuilding,
  onSelectFloor,
  unmappedRooms,
  onSelectRoom,
  selectedRoomId,
  editable,
  run,
}: {
  facility: Facility
  selectedBuildingId: string | null
  selectedFloorId: string | null
  isExpanded: (id: string) => boolean
  onToggle: (id: string) => void
  onSelectBuilding: (id: string) => void
  onSelectFloor: (buildingId: string, floorId: string) => void
  unmappedRooms: LocationRoom[]
  onSelectRoom: (id: string) => void
  selectedRoomId: string | null
  editable: boolean
  run: Run
}) {
  const mut = useFacilityMutations()
  const [adding, setAdding] = useState(false)
  const [name, setName] = useState('')
  const [dragId, setDragId] = useState<string | null>(null)
  const [overId, setOverId] = useState<string | null>(null)

  // A building dropped on another takes its place; each moved building's
  // display order is saved, which is the order the site's lists use.
  const reorder = (movingId: string, targetId: string) => {
    const ids = facility.buildings.map((b) => b.id)
    const from = ids.indexOf(movingId)
    const to = ids.indexOf(targetId)
    if (from < 0 || to < 0) return
    ids.splice(to, 0, ...ids.splice(from, 1))
    const moved = ids.filter((id, i) => facility.buildings[i]?.id !== id)
    void run(async () => {
      for (const id of moved) await mut.updateBuilding.mutateAsync({ id, sortOrder: ids.indexOf(id) })
    })
  }

  const addBuilding = (e: React.FormEvent) => {
    e.preventDefault()
    const next = name.trim()
    if (!next) return
    void run(async () => {
      const created = await mut.createBuilding.mutateAsync({
        facilityId: facility.id,
        code: buildingCodeFrom(next, facility.buildings.map((b) => b.code)),
        name: next,
      })
      onSelectBuilding(created.id)
      setName('')
      setAdding(false)
    })
  }

  return (
    <aside className='flex min-h-0 flex-col gap-3 overflow-y-auto border-r bg-white p-4'>
      <div className='flex items-center justify-between'>
        <h3 className='text-sm font-semibold text-slate-900'>
          Buildings in {facility.code}
          {editable && facility.buildings.length > 1 && <span className='block text-[11px] font-normal text-slate-500'>Drag to reorder</span>}
        </h3>
        {editable && (
          <Button variant='outline' size='sm' className='h-8 w-8 p-0' aria-label='Add building' onClick={() => setAdding((a) => !a)}>
            <Plus className='h-4 w-4' />
          </Button>
        )}
      </div>

      {adding && (
        <form onSubmit={addBuilding} className='flex gap-1.5'>
          <Input autoFocus value={name} onChange={(e) => setName(e.target.value)} placeholder='e.g. Building 2' className='h-9' aria-label='New building name' />
          <Button type='submit' size='sm' className='h-9' disabled={!name.trim() || mut.createBuilding.isPending}>
            Add
          </Button>
        </form>
      )}

      <ul className='space-y-2'>
        {facility.buildings.map((b) => {
          const open = isExpanded(b.id)
          const rooms = b.floors.reduce((n, f) => n + f.rooms.length, 0)
          const selected = b.id === selectedBuildingId
          return (
            <li
              key={b.id}
              draggable={editable}
              onDragStart={(e) => {
                e.dataTransfer.setData('text/plain', b.id)
                e.dataTransfer.effectAllowed = 'move'
                setDragId(b.id)
              }}
              onDragEnd={() => {
                setDragId(null)
                setOverId(null)
              }}
              onDragOver={(e) => {
                if (!dragId || dragId === b.id) return
                e.preventDefault()
                setOverId(b.id)
              }}
              onDrop={(e) => {
                e.preventDefault()
                if (dragId && dragId !== b.id) reorder(dragId, b.id)
                setDragId(null)
                setOverId(null)
              }}
              className={cn(
                'overflow-hidden rounded-xl border',
                selected ? 'border-primary ring-1 ring-primary/30' : 'border-slate-200',
                editable && 'cursor-grab',
                dragId === b.id && 'opacity-50',
                overId === b.id && 'border-t-4 border-t-primary',
              )}
            >
              <div className={cn('flex items-center gap-2.5 px-3 py-2.5', selected ? 'bg-primary/5' : 'bg-white')}>
                <button type='button' onClick={() => onSelectBuilding(b.id)} className='flex min-w-0 flex-1 items-center gap-2.5 text-left'>
                  <MiniStack building={b} />
                  <span className='min-w-0'>
                    <span className='block truncate text-sm font-semibold text-slate-900'>{b.name}</span>
                    <span className='block text-[11px] text-slate-500'>
                      {b.floors.length} {b.floors.length === 1 ? 'floor' : 'floors'}, {rooms} {rooms === 1 ? 'room' : 'rooms'}
                    </span>
                  </span>
                </button>
                <button
                  type='button'
                  onClick={() => onToggle(b.id)}
                  className='rounded p-1 text-slate-500 hover:bg-slate-100'
                  aria-label={open ? `Hide floors of ${b.name}` : `Show floors of ${b.name}`}
                  aria-expanded={open}
                >
                  <ChevronDown className={cn('h-4 w-4 transition-transform', open && 'rotate-180')} />
                </button>
              </div>
              {open && (
                <div className='space-y-1 border-t border-slate-100 bg-slate-50/60 p-1.5'>
                  {[...b.floors]
                    .sort((x, y) => y.level - x.level)
                    .map((f) => {
                      const on = f.id === selectedFloorId && selected
                      return (
                        <button
                          key={f.id}
                          type='button'
                          onClick={() => onSelectFloor(b.id, f.id)}
                          className={cn(
                            'flex w-full items-center gap-2.5 rounded-lg border-l-2 px-2.5 py-2 text-left',
                            on ? 'border-primary bg-white shadow-sm' : 'border-transparent hover:bg-white',
                          )}
                        >
                          <span
                            className={cn(
                              'flex h-7 w-7 items-center justify-center rounded-md text-xs font-semibold tabular-nums',
                              on ? 'bg-primary text-primary-foreground' : 'bg-white text-slate-600 ring-1 ring-slate-200',
                            )}
                          >
                            {f.level}
                          </span>
                          <span className='min-w-0'>
                            <span className={cn('block truncate text-sm', on ? 'font-semibold text-primary' : 'text-slate-800')}>{f.name}</span>
                            <span className='block text-[11px] text-slate-500'>
                              {f.rooms.length} {f.rooms.length === 1 ? 'room' : 'rooms'}
                            </span>
                          </span>
                        </button>
                      )
                    })}
                  {editable && (
                    <button
                      type='button'
                      disabled={mut.createFloor.isPending}
                      onClick={() =>
                        void run(async () => {
                          const next = nextFloorUp(b.floors.map((f) => f.level))
                          const created = await mut.createFloor.mutateAsync({ buildingId: b.id, ...next })
                          onSelectFloor(b.id, created.id)
                        })
                      }
                      className='flex w-full items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-xs font-medium text-primary hover:bg-white disabled:opacity-50'
                    >
                      <Plus className='h-3.5 w-3.5' /> Add floor
                    </button>
                  )}
                  {b.floors.length === 0 && !editable && <p className='px-2.5 py-1.5 text-xs text-slate-500'>No floors yet.</p>}
                </div>
              )}
            </li>
          )
        })}
      </ul>

      {unmappedRooms.length > 0 && (
        <section className='space-y-1.5 border-t pt-3'>
          <h4 className='text-xs font-medium text-slate-500'>Rooms without a floor</h4>
          {unmappedRooms.map((r) => (
            <button
              key={r.id}
              type='button'
              onClick={() => onSelectRoom(r.id)}
              className={cn(
                'flex w-full items-center gap-2 rounded-lg border px-2.5 py-1.5 text-left text-sm',
                r.id === selectedRoomId ? 'border-primary bg-primary/5' : 'border-slate-200 hover:bg-slate-50',
              )}
            >
              <DoorOpen className='h-4 w-4 text-slate-400' /> {r.label}
            </button>
          ))}
        </section>
      )}
    </aside>
  )
}

/** A building in miniature: one bar per floor, filled when it has rooms. */
function MiniStack({ building }: { building: Building }) {
  const floors = [...building.floors].sort((a, b) => b.level - a.level).slice(0, 5)
  return (
    <span className='flex h-11 w-11 shrink-0 flex-col justify-end gap-0.5 rounded-lg bg-slate-100 p-1.5' aria-hidden>
      <span className='h-1 rounded-sm bg-slate-700' />
      {floors.length === 0 ? (
        <span className='h-1.5 rounded-sm border border-dashed border-slate-300' />
      ) : (
        floors.map((f) => <span key={f.id} className={cn('h-1.5 rounded-sm', f.rooms.length ? 'bg-primary/70' : 'bg-slate-300')} />)
      )}
    </span>
  )
}

// ── Middle: the floor ────────────────────────────────────────────────────────

function floorTotals(rooms: LocationRoom[], byEquipment: Map<string, CapacitySummaryRow>) {
  const units = rooms.flatMap((r) => r.units)
  const caps = units.map((u) => byEquipment.get(u.id)).filter((c): c is CapacitySummaryRow => !!c)
  const positions = caps.reduce((n, c) => n + c.spaces, 0)
  const used = caps.reduce((n, c) => n + c.used, 0)
  return { rooms: rooms.length, units: units.length, positions, free: positions - used }
}

function FloorHeader({
  facility,
  building,
  floor,
  byEquipment,
  view,
  onViewChange,
  roomEnabled,
  editable,
  run,
}: {
  facility: Facility
  building: Building
  floor: Floor | null
  byEquipment: Map<string, CapacitySummaryRow>
  view: ExplorerView
  onViewChange: (v: ExplorerView) => void
  roomEnabled: boolean
  editable: boolean
  run: Run
}) {
  const mut = useFacilityMutations()
  const [renaming, setRenaming] = useState(false)
  const [name, setName] = useState(floor?.name ?? '')
  useEffect(() => setName(floor?.name ?? ''), [floor?.id, floor?.name])
  const totals = floorTotals(floor?.rooms ?? [], byEquipment)

  return (
    <div className='flex flex-wrap items-start gap-4'>
      <div className='min-w-0 flex-1'>
        <h2 className='flex flex-wrap items-center gap-2 text-xl font-semibold text-slate-900'>
          {building.name}
          {floor && (
            <>
              <ChevronRight className='h-5 w-5 text-slate-400' />
              {renaming ? (
                <form
                  className='flex items-center gap-1'
                  onSubmit={(e) => {
                    e.preventDefault()
                    if (name.trim() && name.trim() !== floor.name) void run(() => mut.updateFloor.mutateAsync({ id: floor.id, name: name.trim() }))
                    setRenaming(false)
                  }}
                >
                  <Input autoFocus value={name} onChange={(e) => setName(e.target.value)} className='h-8 w-48 text-base' aria-label='Floor name' />
                  <Button type='submit' size='sm' variant='ghost' className='h-8 w-8 p-0' aria-label='Save floor name'>
                    <Check className='h-4 w-4' />
                  </Button>
                </form>
              ) : (
                <span>{floor.name}</span>
              )}
              {editable && !renaming && (
                <button type='button' onClick={() => setRenaming(true)} className='rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700' aria-label={`Rename ${floor.name}`}>
                  <Pencil className='h-4 w-4' />
                </button>
              )}
              {editable && !renaming && (
                <button
                  type='button'
                  onClick={() => {
                    const note = floor.rooms.length ? ' Its rooms stay, without a floor, to place again.' : ''
                    if (window.confirm(`Remove ${floor.name}?${note}`)) void run(() => mut.deleteFloor.mutateAsync(floor.id))
                  }}
                  className='rounded p-1 text-slate-400 hover:bg-red-50 hover:text-red-600'
                  aria-label={`Remove ${floor.name}`}
                >
                  <Trash2 className='h-4 w-4' />
                </button>
              )}
            </>
          )}
        </h2>
        <p className='mt-0.5 flex items-center gap-1 text-sm text-slate-500'>
          <MapPin className='h-3.5 w-3.5' /> {facility.code} {facility.name}
        </p>
      </div>

      <dl className='flex items-center divide-x rounded-xl border bg-white'>
        {(
          [
            ['Rooms', totals.rooms, DoorOpen],
            ['Units', totals.units, Box],
            ['Positions', totals.positions, Layers],
          ] as const
        ).map(([label, value, Icon]) => (
          <div key={label} className='flex items-center gap-2 px-3.5 py-2'>
            <Icon className='h-4 w-4 text-slate-400' />
            <div>
              <dd className='text-base font-semibold leading-none tabular-nums text-slate-900'>{value}</dd>
              <dt className='mt-0.5 text-[11px] text-slate-500'>{label}</dt>
            </div>
          </div>
        ))}
      </dl>

      <ViewToggle value={view} onChange={onViewChange} roomEnabled={roomEnabled} />
    </div>
  )
}

function Legend({ focus }: { focus: boolean }) {
  const items: [string, string][] = [
    ['bg-teal-300', 'Holds units'],
    ['bg-amber-400', 'Out of service'],
    ['bg-slate-300', 'Empty'],
    ...(focus ? ([['bg-blue-600', 'This unit']] as [string, string][]) : []),
  ]
  return (
    <div className='absolute bottom-3 left-3 flex flex-wrap items-center gap-3 rounded-lg border bg-white/95 px-3 py-1.5 text-xs text-slate-600 shadow-sm'>
      {items.map(([color, label]) => (
        <span key={label} className='flex items-center gap-1.5'>
          <span className={cn('h-2.5 w-2.5 rounded-full', color)} aria-hidden /> {label}
        </span>
      ))}
    </div>
  )
}

function RoomsOnFloor({
  floor,
  tree,
  byEquipment,
  selectedRoomId,
  onSelectRoom,
  focusUnitId,
  editable,
  run,
}: {
  floor: Floor
  tree: LocationTree
  byEquipment: Map<string, CapacitySummaryRow>
  selectedRoomId: string | null
  onSelectRoom: (id: string) => void
  focusUnitId: string | null
  editable: boolean
  run: Run
}) {
  const [query, setQuery] = useState('')
  const q = query.trim().toLowerCase()
  const rooms = q
    ? floor.rooms.filter((r) => [r.label, r.name, ...r.units.map((u) => u.label)].some((t) => t?.toLowerCase().includes(q)))
    : floor.rooms

  return (
    <section className='space-y-3'>
      <div className='flex flex-wrap items-center gap-3'>
        <h3 className='text-base font-semibold text-slate-900'>Rooms on {floor.name}</h3>
        <div className='relative ml-auto w-full max-w-xs'>
          <Search className='pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-slate-400' />
          <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder='Search rooms…' className='h-9 pl-8' aria-label='Search rooms' />
        </div>
      </div>
      <div className='grid gap-3 sm:grid-cols-2 2xl:grid-cols-3'>
        {rooms.map((r) => (
          <RoomCard
            key={r.id}
            room={r}
            byEquipment={byEquipment}
            selected={r.id === selectedRoomId}
            holdsFocus={!!focusUnitId && r.units.some((u) => u.id === focusUnitId)}
            onSelect={() => onSelectRoom(r.id)}
          />
        ))}
        {editable && !q && (
          <AddRoomCard floorId={floor.id} taken={allRooms(tree).map((r) => r.label)} onCreated={onSelectRoom} run={run} />
        )}
      </div>
      {rooms.length === 0 && q && <p className='text-sm text-slate-500'>No room on {floor.name} matches “{query}”.</p>}
    </section>
  )
}

function StatusPill({ down }: { down: boolean }) {
  return (
    <span
      className={cn(
        'inline-flex w-fit items-center gap-1.5 rounded-full px-2 py-0.5 text-[11px] font-medium',
        down ? 'bg-amber-50 text-amber-800' : 'bg-emerald-50 text-emerald-800',
      )}
    >
      <span className={cn('h-1.5 w-1.5 rounded-full', down ? 'bg-amber-500' : 'bg-emerald-500')} aria-hidden />
      {down ? 'Out of service' : 'In service'}
    </span>
  )
}

function RoomStats({ room, byEquipment, size = 'sm' }: { room: LocationRoom; byEquipment: Map<string, CapacitySummaryRow>; size?: 'sm' | 'lg' }) {
  const t = floorTotals([room], byEquipment)
  return (
    <dl className={cn('grid grid-cols-3', size === 'lg' ? 'gap-2' : 'gap-1')}>
      {(
        [
          ['Units', t.units],
          ['Positions', t.positions],
          ['Free', t.free],
        ] as const
      ).map(([label, value]) => (
        <div key={label} className={cn(size === 'lg' && 'rounded-lg bg-slate-50 px-3 py-2')}>
          <dd className={cn('font-semibold tabular-nums text-slate-900', size === 'lg' ? 'text-lg' : 'text-sm')}>{value}</dd>
          <dt className='text-[11px] text-slate-500'>{label}</dt>
        </div>
      ))}
    </dl>
  )
}

function RoomCard({
  room,
  byEquipment,
  selected,
  holdsFocus,
  onSelect,
}: {
  room: LocationRoom
  byEquipment: Map<string, CapacitySummaryRow>
  selected: boolean
  holdsFocus: boolean
  onSelect: () => void
}) {
  return (
    <button
      type='button'
      onClick={onSelect}
      aria-pressed={selected}
      className={cn(
        'flex flex-col gap-2 rounded-xl border bg-white p-4 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary',
        selected ? 'border-primary ring-2 ring-primary/20' : 'border-slate-200 hover:border-slate-300',
      )}
    >
      <span className='flex items-center justify-between gap-2'>
        <StatusPill down={room.status === 'OUT_OF_SERVICE'} />
        {holdsFocus && <span className='rounded-full bg-blue-600 px-2 py-0.5 text-[11px] font-semibold text-white'>This unit is here</span>}
      </span>
      <span>
        <span className='block text-base font-semibold text-slate-900'>{room.label}</span>
        <span className='block text-sm text-slate-500'>{room.name ?? room.typeName}</span>
      </span>
      <RoomStats room={room} byEquipment={byEquipment} />
    </button>
  )
}

/** The "Add room" card: one click names it, Enter makes it. */
function AddRoomCard({ floorId, taken, onCreated, run }: { floorId: string; taken: string[]; onCreated: (id: string) => void; run: Run }) {
  const types = useRoomTypes()
  const createRoom = useCreateRoom()
  const createType = useCreateRoomType()
  const [editing, setEditing] = useState(false)
  const [label, setLabel] = useState('')
  const [name, setName] = useState('')
  const [typeId, setTypeId] = useState('')
  const typeList = types.data ?? []
  const chosenType = typeId || typeList[0]?.id || ''

  if (!editing) {
    return (
      <button
        type='button'
        onClick={() => {
          setLabel(nextRoomLabel(taken))
          setName('')
          setEditing(true)
        }}
        className='flex min-h-[140px] flex-col items-center justify-center gap-1.5 rounded-xl border-2 border-dashed border-slate-300 p-4 text-center hover:border-primary hover:bg-primary/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary'
      >
        <span className='flex h-9 w-9 items-center justify-center rounded-lg bg-primary/10 text-primary'>
          <Plus className='h-5 w-5' />
        </span>
        <span className='text-sm font-semibold text-primary'>Add room</span>
        <span className='text-xs text-slate-500'>A new room on this floor</span>
      </button>
    )
  }

  if (!types.isLoading && typeList.length === 0) {
    return (
      <div className='space-y-2 rounded-xl border border-dashed border-amber-300 bg-amber-50 p-4 text-sm text-amber-900'>
        <p>Rooms need a room type first.</p>
        <Button size='sm' disabled={createType.isPending} onClick={() => void run(() => createType.mutateAsync())}>
          Create a “Room” type
        </Button>
      </div>
    )
  }

  return (
    <form
      className='space-y-2 rounded-xl border border-primary bg-white p-4 ring-2 ring-primary/20'
      onSubmit={(e) => {
        e.preventDefault()
        if (!label.trim() || !chosenType) return
        void run(async () => {
          const created = await createRoom.mutateAsync({ label: label.trim(), name: name.trim() || undefined, typeId: chosenType, floorId })
          onCreated(created.id)
          setEditing(false)
        })
      }}
    >
      <label className='block text-xs font-medium text-slate-600'>
        Room ID
        <Input autoFocus value={label} onChange={(e) => setLabel(e.target.value)} className='mt-1 h-8' />
      </label>
      <label className='block text-xs font-medium text-slate-600'>
        What it is
        <Input value={name} onChange={(e) => setName(e.target.value)} placeholder='e.g. Cryo Storage' className='mt-1 h-8' />
      </label>
      {typeList.length > 1 && (
        <Select value={chosenType} onValueChange={setTypeId}>
          <SelectTrigger className='h-8 text-xs' aria-label='Room type'>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {typeList.map((t) => (
              <SelectItem key={t.id} value={t.id}>
                {t.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}
      <div className='flex justify-end gap-1.5'>
        <Button type='button' size='sm' variant='ghost' onClick={() => setEditing(false)}>
          Cancel
        </Button>
        <Button type='submit' size='sm' disabled={!label.trim() || createRoom.isPending}>
          {createRoom.isPending ? 'Adding…' : 'Add room'}
        </Button>
      </div>
    </form>
  )
}

// ── Right: the room ──────────────────────────────────────────────────────────

function RoomPanel({
  tree,
  facility,
  room,
  byEquipment,
  focusUnitId,
  selectedUnitId,
  onSelectUnit,
  onArrange,
  editable,
  onClose,
  run,
}: {
  tree: LocationTree
  facility: Facility
  room: LocationRoom | null
  byEquipment: Map<string, CapacitySummaryRow>
  focusUnitId: string | null
  selectedUnitId: string | null
  onSelectUnit: (id: string) => void
  /** Opens Room 3D on this room. */
  onArrange: () => void
  editable: boolean
  onClose: () => void
  run: Run
}) {
  const mut = useFacilityMutations()
  const rename = useRenameRoom()
  const setRoom = useSetEquipmentRoom()
  const place = useMemo(() => (room ? indexRooms(tree).get(room.id) : undefined), [tree, room])
  const floorId = place && place.length === 4 ? place[2].id : ''
  const [label, setLabel] = useState('')
  const [name, setName] = useState('')
  const [adding, setAdding] = useState(false)
  useEffect(() => {
    setLabel(room?.label ?? '')
    setName(room?.name ?? '')
    setAdding(false)
  }, [room?.id, room?.label, room?.name])

  if (!room) {
    return (
      <aside className='hidden min-h-0 flex-col items-center justify-center gap-2 border-l bg-white p-6 text-center xl:flex'>
        <DoorOpen className='h-8 w-8 text-slate-300' />
        <p className='text-sm font-medium text-slate-900'>Select a room</p>
        <p className='text-sm text-slate-500'>Click a room on the floor or in the list to see what is in it.</p>
      </aside>
    )
  }

  const floorOptions = facility.buildings.flatMap((b) =>
    [...b.floors].sort((x, y) => y.level - x.level).map((f) => ({ id: f.id, label: `${b.name} › ${f.name}` })),
  )
  const dirty = label.trim() !== room.label || name.trim() !== (room.name ?? '')
  const units = [...room.units].sort((a, b) => Number(b.id === focusUnitId) - Number(a.id === focusUnitId))

  return (
    <aside className='flex min-h-0 flex-col gap-4 overflow-y-auto border-l bg-white p-5'>
      <div className='flex items-start gap-3'>
        <span className='flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary'>
          <Building2 className='h-5 w-5' />
        </span>
        <div className='min-w-0 flex-1'>
          <h3 className='flex flex-wrap items-center gap-2 text-lg font-semibold text-slate-900'>
            {room.label} <StatusPill down={room.status === 'OUT_OF_SERVICE'} />
          </h3>
          <p className='flex items-center gap-1 text-xs text-slate-500'>
            <MapPin className='h-3 w-3 shrink-0' />
            <span className='truncate'>{place ? formatPlace(place) : room.label}</span>
          </p>
        </div>
        <button type='button' onClick={onClose} className='rounded p-1 text-slate-400 hover:bg-slate-100' aria-label='Close room'>
          <X className='h-4 w-4' />
        </button>
      </div>

      <form
        className='grid grid-cols-2 gap-3'
        onSubmit={(e) => {
          e.preventDefault()
          if (!dirty || !label.trim()) return
          void run(() =>
            rename.mutateAsync({
              id: room.id,
              ...(label.trim() !== room.label ? { label: label.trim() } : {}),
              ...(name.trim() !== (room.name ?? '') ? { name: name.trim() } : {}),
            }),
          )
        }}
      >
        <label className='text-xs font-medium text-slate-600'>
          Room ID
          <Input value={label} onChange={(e) => setLabel(e.target.value)} disabled={!editable} className='mt-1 h-9' />
        </label>
        <label className='text-xs font-medium text-slate-600'>
          What it is
          <Input value={name} onChange={(e) => setName(e.target.value)} disabled={!editable} placeholder='e.g. Cryo Storage' className='mt-1 h-9' />
        </label>
        <label className='col-span-2 text-xs font-medium text-slate-600'>
          Floor
          <Select
            value={floorId || 'none'}
            disabled={!editable}
            onValueChange={(v) => void run(() => mut.setRoomFloor.mutateAsync({ roomId: room.id, floorId: v === 'none' ? null : v }))}
          >
            <SelectTrigger className='mt-1 h-9'>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value='none'>No floor yet</SelectItem>
              {floorOptions.map((f) => (
                <SelectItem key={f.id} value={f.id}>
                  {f.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </label>
        {editable && dirty && (
          <Button type='submit' size='sm' className='col-span-2 w-fit' disabled={rename.isPending}>
            Save
          </Button>
        )}
      </form>

      <RoomStats room={room} byEquipment={byEquipment} size='lg' />

      <RoomLayout room={room} editable={editable} onArrange={onArrange} run={run} />

      <section className='space-y-2'>
        <div className='flex items-center justify-between'>
          <h4 className='text-sm font-semibold text-slate-900'>Units in this room ({room.units.length})</h4>
          {editable && tree.unplaced.length > 0 && (
            <Button variant='outline' size='sm' className='h-8 gap-1' onClick={() => setAdding((a) => !a)}>
              <Plus className='h-3.5 w-3.5' /> Add unit
            </Button>
          )}
        </div>
        {adding && (
          <Select
            value=''
            onValueChange={(id) => void run(async () => {
              await setRoom.mutateAsync({ equipmentId: id, roomId: room.id })
              setAdding(false)
            })}
          >
            <SelectTrigger className='h-9 text-sm' aria-label='Unit to put in this room'>
              <SelectValue placeholder={`Choose one of ${tree.unplaced.length} not in a room`} />
            </SelectTrigger>
            <SelectContent>
              {tree.unplaced.map((u) => (
                <SelectItem key={u.id} value={u.id}>
                  {u.label} <span className='text-muted-foreground'>· {u.typeName}</span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
        {room.units.length === 0 && <p className='text-sm text-slate-500'>No units in this room yet.</p>}
        <ul className='space-y-1.5'>
          {units.map((u) => (
            <UnitRow
              key={u.id}
              unit={u}
              capacity={byEquipment.get(u.id)}
              focus={u.id === focusUnitId}
              picked={u.id === selectedUnitId}
              onPick={() => onSelectUnit(u.id)}
              onShape={editable ? (shape) => void run(() => mut.setUnitSpot.mutateAsync({ equipmentId: u.id, shape })) : undefined}
              onRemove={editable ? () => void run(() => setRoom.mutateAsync({ equipmentId: u.id, roomId: null })) : undefined}
            />
          ))}
        </ul>
      </section>

      {/* The picked unit's inside, or the unit this was opened from. */}
      {(() => {
        const shownId = selectedUnitId ?? focusUnitId
        const unit = room.units.find((u) => u.id === shownId)
        const capacity = unit ? byEquipment.get(unit.id) : undefined
        return unit && capacity ? <UnitInside key={unit.id} unit={unit} capacity={capacity} /> : null
      })()}
    </aside>
  )
}

/**
 * The room's layout: open it in 3D to arrange what is inside, and set its size
 * by hand when the area does not give the right shape.
 */
function RoomLayout({ room, editable, onArrange, run }: { room: LocationRoom; editable: boolean; onArrange: () => void; run: Run }) {
  const mut = useFacilityMutations()
  const auto = roomFootprint({ areaSqm: room.areaSqm ?? null, unitCount: room.units.length, size: null })
  const current = room.size ?? auto
  const [w, setW] = useState(String(current.w))
  const [d, setD] = useState(String(current.d))
  useEffect(() => {
    setW(String(current.w))
    setD(String(current.d))
  }, [room.id, current.w, current.d])
  const nw = Number(w)
  const nd = Number(d)
  const valid = Number.isInteger(nw) && Number.isInteger(nd) && nw >= 2 && nd >= 2 && nw <= 40 && nd <= 40
  const dirty = valid && (nw !== current.w || nd !== current.d)

  return (
    <section className='space-y-2 rounded-xl border border-slate-200 p-3'>
      <div className='flex items-center justify-between gap-2'>
        <h4 className='text-sm font-semibold text-slate-900'>Room layout</h4>
        <Button size='sm' variant='outline' className='h-8 gap-1' onClick={onArrange}>
          <Box className='h-3.5 w-3.5' /> {editable ? 'Arrange in 3D' : 'View in 3D'}
        </Button>
      </div>
      {editable && (
        <label className='flex items-center gap-2 text-xs font-medium text-slate-600'>
          Room shape
          <Select
            value={toOutline(room.outline)}
            onValueChange={(v) =>
              void run(() =>
                mut.setRoomSize.mutateAsync({ roomId: room.id, w: room.size?.w ?? null, d: room.size?.d ?? null, outline: v === 'rect' ? null : v }),
              )
            }
          >
            <SelectTrigger className='h-8 w-44 text-xs' aria-label='Room shape'>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {ROOM_OUTLINES.map((o) => (
                <SelectItem key={o} value={o}>
                  {ROOM_OUTLINE_LABELS[o]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </label>
      )}
      {editable && (
        <form
          className='flex flex-wrap items-end gap-2'
          onSubmit={(e) => {
            e.preventDefault()
            if (dirty) void run(() => mut.setRoomSize.mutateAsync({ roomId: room.id, w: nw, d: nd }))
          }}
        >
          <label className='text-xs font-medium text-slate-600'>
            Width
            <Input value={w} onChange={(e) => setW(e.target.value)} inputMode='numeric' className='mt-1 h-8 w-16' aria-label='Room width in cells' />
          </label>
          <span className='pb-2 text-slate-400'>×</span>
          <label className='text-xs font-medium text-slate-600'>
            Depth
            <Input value={d} onChange={(e) => setD(e.target.value)} inputMode='numeric' className='mt-1 h-8 w-16' aria-label='Room depth in cells' />
          </label>
          <span className='pb-2 text-xs text-slate-500'>cells</span>
          {dirty && (
            <Button type='submit' size='sm' className='h-8'>
              Save size
            </Button>
          )}
          {room.size && !dirty && (
            <Button type='button' size='sm' variant='ghost' className='h-8 text-xs' onClick={() => void run(() => mut.setRoomSize.mutateAsync({ roomId: room.id, w: null, d: null }))}>
              Size from area
            </Button>
          )}
        </form>
      )}
      {editable && !valid && <p className='text-xs text-destructive'>Width and depth are whole cells, 2 to 40.</p>}
    </section>
  )
}

function UnitRow({
  unit,
  capacity,
  focus,
  picked,
  onPick,
  onShape,
  onRemove,
}: {
  unit: PlacedUnit
  capacity?: CapacitySummaryRow
  focus: boolean
  /** Picked in the room view: lit there and here. */
  picked: boolean
  onPick: () => void
  /** Change what it is drawn as; omitted, the shape is only shown. */
  onShape?: (shape: UnitShape | null) => void
  onRemove?: () => void
}) {
  const down = unit.status === 'OUT_OF_SERVICE' || !!capacity?.unavailable
  const pct = capacity && capacity.spaces > 0 ? Math.round((capacity.used / capacity.spaces) * 100) : null
  const shape = shapeOf(unit)
  return (
    <li
      className={cn(
        'space-y-2 rounded-xl border px-3 py-2.5',
        focus ? 'border-blue-600 bg-blue-50 ring-1 ring-blue-600' : picked ? 'border-emerald-500 bg-emerald-50/60 ring-1 ring-emerald-500' : 'border-slate-200 bg-white',
      )}
    >
      <div className='flex items-center gap-3'>
        <span className={cn('h-2.5 w-2.5 shrink-0 rounded-full', down ? 'bg-amber-500' : 'bg-emerald-500')} aria-hidden />
        <button type='button' onClick={onPick} className='flex min-w-0 flex-1 items-center gap-3 text-left' aria-pressed={picked}>
          <span className='flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-slate-100 text-slate-500'>
            <Box className='h-4 w-4' />
          </span>
          <span className='min-w-0 flex-1'>
            <span className='flex items-center gap-1.5'>
              <span className='truncate text-sm font-semibold text-slate-900'>{unit.label}</span>
              {focus && <span className='rounded bg-blue-600 px-1.5 text-[10px] font-semibold text-white'>This unit</span>}
            </span>
            <span className='block truncate text-xs text-slate-500'>
              {unit.typeName}
              {pct !== null && `, ${pct}% full`}
            </span>
          </span>
        </button>
        {onRemove && (
          <button
            type='button'
            onClick={onRemove}
            className='rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700'
            aria-label={`Take ${unit.label} out of this room`}
          >
            <X className='h-4 w-4' />
          </button>
        )}
        {capacity && (
          <Link to={`/inventory/storage/${capacity.nodeId}`} className='rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700' aria-label={`Open ${unit.label}`}>
            <ChevronRight className='h-4 w-4' />
          </Link>
        )}
      </div>
      {onShape && (
        <label className='flex items-center gap-2 pl-[22px] text-xs text-slate-500'>
          Drawn as
          <Select value={unit.shape ? shape : 'auto'} onValueChange={(v) => onShape(v === 'auto' ? null : (v as UnitShape))}>
            <SelectTrigger className='h-7 w-48 text-xs' aria-label={`Shape of ${unit.label}`}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value='auto'>Automatic: {UNIT_SHAPE_LABELS[guessShape(unit.typeName, unit.purpose)]}</SelectItem>
              {UNIT_SHAPES.map((s) => (
                <SelectItem key={s} value={s}>
                  {UNIT_SHAPE_LABELS[s]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </label>
      )}
    </li>
  )
}

/**
 * What is inside the picked storage unit (z8nrz7fw25): its zones, racks and
 * boxes as nested blocks with how full each is. Arranging them stays on the
 * unit's own page: a rack's place is part of the address stock is filed
 * under, so moving it is a storage change, not a drawing one.
 */
function UnitInside({ unit, capacity }: { unit: PlacedUnit; capacity: CapacitySummaryRow }) {
  const subtree = useStorageSubtree(capacity.nodeId)
  const outline = useMemo(() => (subtree.data ? storageOutline(subtree.data, capacity.nodeId) : []), [subtree.data, capacity.nodeId])

  return (
    <section className='space-y-2 rounded-xl border border-emerald-200 bg-emerald-50/40 p-3'>
      <div className='flex items-center justify-between gap-2'>
        <h4 className='text-sm font-semibold text-slate-900'>Inside {unit.label}</h4>
        <Link to={`/inventory/storage/${capacity.nodeId}`} className='text-xs font-medium text-primary hover:underline'>
          Arrange inside
        </Link>
      </div>
      {subtree.isLoading ? (
        <p className='text-xs text-slate-500'>Loading…</p>
      ) : outline.length === 0 ? (
        <p className='text-xs text-slate-500'>Nothing set up inside yet.</p>
      ) : (
        <div className='flex flex-wrap gap-2'>
          {outline.map((n) => (
            <OutlineBlock key={n.id} node={n} />
          ))}
        </div>
      )}
    </section>
  )
}

function OutlineBlock({ node }: { node: OutlineNode }) {
  const pct = node.positions ? Math.round((node.used / node.positions) * 100) : 0
  return (
    <div className='min-w-[88px] flex-1 rounded-lg border border-slate-200 bg-white p-2'>
      <p className='truncate text-xs font-semibold text-slate-800' title={node.label}>
        {node.label}
      </p>
      <p className='text-[10px] text-slate-500'>
        {node.kind.charAt(0) + node.kind.slice(1).toLowerCase()}, {node.used}/{node.positions}
      </p>
      <div className='mt-1 h-1 overflow-hidden rounded-full bg-slate-200'>
        <div className={cn('h-full rounded-full', pct >= 90 ? 'bg-red-500' : pct >= 70 ? 'bg-amber-500' : 'bg-emerald-500')} style={{ width: `${pct}%` }} />
      </div>
      {node.children.length > 0 && (
        <div className='mt-1.5 flex flex-wrap gap-1'>
          {node.children.map((c) => (
            <span
              key={c.id}
              title={`${c.label}: ${c.used} of ${c.positions} in use`}
              className={cn(
                'rounded border px-1 text-[10px]',
                c.positions && c.used === c.positions ? 'border-red-200 bg-red-50 text-red-700' : 'border-slate-200 bg-slate-50 text-slate-600',
              )}
            >
              {c.label}
            </span>
          ))}
        </div>
      )}
    </div>
  )
}

function EmptyState({ title, text }: { title: string; text: string }) {
  return (
    <div className='flex min-h-[220px] flex-col items-center justify-center gap-1.5 rounded-xl border border-dashed bg-white p-6 text-center'>
      <Building2 className='h-8 w-8 text-slate-300' />
      <p className='text-sm font-medium text-slate-900'>{title}</p>
      {text && <p className='text-sm text-slate-500'>{text}</p>}
    </div>
  )
}
